import { memo } from 'react';
import type { TreeOverlay } from '@/api/medicalOverlay.api';
import { OVERLAY_STATUS_STYLE, OVERLAY_STATUS_ORDER } from './overlayStatus';

interface Props {
  overlay: TreeOverlay;
  onClose: () => void;
}

export const OverlayLegend = memo(function OverlayLegend({ overlay, onClose }: Props) {
  return (
    <div className="absolute left-4 top-4 z-10 w-60 rounded-lg border border-slate-200 bg-white/95 p-3 shadow-md backdrop-blur">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Overlay</div>
          <div className="text-sm font-medium text-slate-800">{overlay.label}</div>
        </div>
        <button onClick={onClose} className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" title="Overlay ausschalten" aria-label="Overlay ausschalten">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <ul className="space-y-1">
        {OVERLAY_STATUS_ORDER.map((status) => {
          const count = overlay.counts[status] ?? 0;
          if (count === 0) return null;
          const style = OVERLAY_STATUS_STYLE[status];
          return (
            <li key={status} className="flex items-center gap-2 text-xs">
              <span
                className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-bold"
                style={{
                  background: style.hatched ? 'repeating-linear-gradient(45deg,#f8fafc,#f8fafc 2px,#cbd5e1 2px,#cbd5e1 4px)' : style.badgeBg,
                  color: style.badgeFg,
                  border: `1.5px solid ${style.ring}`,
                }}
              >
                {style.symbol}
              </span>
              <span className="flex-1 text-slate-600">{style.label}</span>
              <span className="font-medium text-slate-700">{count}</span>
            </li>
          );
        })}
      </ul>

      {overlay.suppressedCount > 0 && (
        <p className="mt-2 rounded bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
          {overlay.suppressedCount} lebende Person(en) ohne Risiko-Anzeige (Recht auf Nichtwissen / fehlende Einwilligung).
        </p>
      )}
      <p className="mt-2 text-[10px] leading-snug text-slate-400">{overlay.disclaimer}</p>
    </div>
  );
});
